import {queryClient} from 'views/QueryClientWrapper';
import {getWorkstreamRoadmapInitiativesQueryKey} from 'views/workstreams/jiraObjects';

import {isProd} from './environment';

// Event-based message structure
export interface BroadcastMessage {
  type: string;
  payload: Record<string, any>;
}

const RESERVED_WORKSTREAM_QUERY_ROOT = new Set(['count', 'parent', 'ancestors', 'logs', 'updates']);

function normalizeWorkstreamBroadcastId(id: unknown): number | null {
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }
  if (typeof id === 'string' && /^\d+$/.test(id)) {
    return Number(id);
  }
  return null;
}

/** True if cached GET /workstream/{id} payload embeds this workstream as a dependency (sub-row). */
function cachedWorkstreamEmbedsDependentId(data: unknown, dependentWorkstreamId: number): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const deps = (data as {dependencies?: Array<{dependent?: {id?: number}}>}).dependencies;
  if (!Array.isArray(deps)) {
    return false;
  }
  return deps.some((d) => d.dependent?.id === dependentWorkstreamId);
}

function cachedExecSponsorInboxContainsWorkstream(data: unknown, workstreamId: number): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const workstreams = (data as {workstreams?: Array<{id?: number}>}).workstreams;
  return (
    Array.isArray(workstreams) && workstreams.some((workstream) => workstream.id === workstreamId)
  );
}

/**
 * Refetch everything that can show workstream rows, status, or labels after a remote change.
 * v2 list queries use `['workstream-data', 'workstreams', paramsObject]`; prefix-only invalidation
 * is easy to miss for infinite queries, so we match the same segments `useWorkstreamStatus` hits locally.
 *
 * When a sub-workstream updates, parent caches (detail + useWorkstream) still hold nested
 * `dependencies[].dependent` snapshots — invalidate those parents too (e.g. planning table embed).
 */
function invalidateRemoteWorkstreamRelatedQueries(workstreamId: number) {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      if (!Array.isArray(key)) {
        return false;
      }

      const cachedData = query.state?.data;

      if (key[0] === 'planning' && key[1] === 'workstreams') {
        return true;
      }

      if (key[0] === 'workstreams' && key[1] === 'exec-sponsor-inbox') {
        if (key.length === 2) {
          return cachedExecSponsorInboxContainsWorkstream(cachedData, workstreamId);
        }
        return key[2] === workstreamId;
      }

      // /workstreams: the rail's nav trees (any of them can hold this workstream), the
      // recents strip's rows, and the north star trend's metric snapshots.
      if (key[0] === 'prototype-nav-tree') {
        return true;
      }
      if (key[0] === 'workstreams4-recent-workstreams') {
        return Array.isArray(key[1]) && key[1].includes(workstreamId);
      }
      if (key[0] === 'workstreams4' && key[1] === 'metric-snapshots') {
        return key[2] === workstreamId;
      }

      if (key[0] === 'workstream-data') {
        const segment = key[1];
        if (segment === 'workstreams' || segment === 'sidebar' || segment === 'audit') {
          return true;
        }
        if (
          segment === 'facets' ||
          segment === 'facet-workstream-options' ||
          segment === 'facet-people-options' ||
          segment === 'facet-team-options' ||
          segment === 'pillars' ||
          segment === 'pillar-configs'
        ) {
          return true;
        }
        if (segment === 'workstream-detail') {
          if (key[2] === workstreamId) {
            return true;
          }
          return cachedWorkstreamEmbedsDependentId(cachedData, workstreamId);
        }
        return false;
      }

      if (key[0] === 'workstream') {
        if (key[1] === 'outcomes_validation') {
          return false;
        }
        if ((key as unknown[]).includes(workstreamId)) {
          return true;
        }
        if (typeof key[1] === 'string' && !RESERVED_WORKSTREAM_QUERY_ROOT.has(key[1])) {
          // Employee tracker / profile scope: `['workstream', '<ldap>', …]`
          return true;
        }
        if (typeof key[1] === 'number') {
          return cachedWorkstreamEmbedsDependentId(cachedData, workstreamId);
        }
        return false;
      }

      return false;
    },
    refetchType: 'all',
  });
}

function invalidateExecSponsorInboxQueries(workstreamId: number) {
  const listQueryKey = ['workstreams', 'exec-sponsor-inbox'] as const;
  const cachedList = queryClient.getQueryData(listQueryKey);

  void queryClient.invalidateQueries({
    queryKey: [...listQueryKey, workstreamId],
    exact: true,
  });

  // Only refresh user's exec sponsor inbox if it contains the updated workstream.
  if (cachedExecSponsorInboxContainsWorkstream(cachedList, workstreamId)) {
    void queryClient.invalidateQueries({queryKey: listQueryKey, exact: true});
  }
}

// Helper to invalidate multiple query keys (TanStack Query defaults to prefix / partial match).
const invalidateKeys = (keys: (string | number | (string | number | undefined)[])[]) => {
  keys.forEach((key) => {
    const queryKey = Array.isArray(key) ? key : [key];
    queryClient.invalidateQueries({queryKey});
  });
};

// Event handlers that determine invalidation keys based on event type and payload
const eventHandlers: Record<string, (payload: Record<string, any>) => void> = {
  news: (payload: Record<string, any>) => {
    const invalidationKeys = [
      ['home', 'news'],
      ['news', payload.id],
      ['news', 'latest'],
    ];

    if (payload.draft) {
      invalidationKeys.push(['news', 'drafts']);
    }

    console.log('News event - invalidating keys:', invalidationKeys);
    invalidateKeys(invalidationKeys);
  },

  workstream: (payload: Record<string, any>) => {
    const workstreamId = normalizeWorkstreamBroadcastId(payload.id);
    if (workstreamId == null) {
      return;
    }

    queryClient.invalidateQueries({queryKey: ['workstreams'], exact: true});
    invalidateRemoteWorkstreamRelatedQueries(workstreamId);

    if (!isProd) {
      console.log(
        'Workstream event — refetching v2 list/detail/sidebar caches for id:',
        workstreamId
      );
    }
  },

  // A view's background Jira refresh changed these workstreams' roadmap items.
  workstreamJira: (payload: Record<string, any>) => {
    const ids: unknown[] = Array.isArray(payload.ids) ? payload.ids : [];
    ids
      .map(normalizeWorkstreamBroadcastId)
      .filter((workstreamId): workstreamId is number => workstreamId !== null)
      .forEach((workstreamId) => {
        void queryClient.invalidateQueries({
          queryKey: getWorkstreamRoadmapInitiativesQueryKey(workstreamId),
        });
      });
  },

  workstreamHealthScore: (payload: Record<string, any>) => {
    const workstreamId = normalizeWorkstreamBroadcastId(payload.workstream_id);
    const checksType = payload.checks_type as string | undefined;
    const taskId = payload.task_id as string | undefined;
    const status = payload.status as string | undefined;
    const skipAIChecks = payload.skip_ai_checks === true;

    const isValidChecksType = checksType && ['default', 'oceo'].includes(checksType);
    const isValidStatus = status && ['processing', 'completed', 'failed'].includes(status);
    if (workstreamId == null || !isValidChecksType || !isValidStatus) {
      return;
    }

    if (taskId && status === 'processing') {
      const pendingTaskData = {taskId, skipAIChecks};
      console.log('Workstream health score pending event - setting pending task:', pendingTaskData);
      queryClient.setQueryData(
        ['workstream', 'health_score', 'pending', workstreamId, checksType],
        pendingTaskData
      );
      return;
    }

    if (status === 'failed') {
      // Clear pending task ID when generation fails
      queryClient.setQueryData(
        ['workstream', 'health_score', 'pending', workstreamId, checksType],
        null
      );
      return;
    }

    const invalidationKeys = [['workstream', 'health_score', workstreamId, checksType]];
    if (checksType === 'default') {
      // Audit reads the latest persisted default readiness score. Keep an open Audit table in
      // sync with scores generated in this or another browser, and mark an inactive table stale.
      invalidationKeys.push(['workstream-data', 'audit']);

      // Exec sponsor inbox renders readiness info, so we need to invalidate it
      // whenever the score and checks change for a workstream.
      invalidateExecSponsorInboxQueries(workstreamId);
    }
    console.log('Workstream health score event - invalidating keys:', invalidationKeys);
    invalidateKeys(invalidationKeys);
  },

  workstreamOutcomesValidation: (payload: Record<string, any>) => {
    const checksType = payload.checks_type as string | undefined;
    const metricId = payload.metric_id ? String(payload.metric_id).trim() : undefined;
    const status = payload.status as string | undefined;

    const isValidChecksType = checksType === 'outcomes_validation_outcomes';
    if (!isValidChecksType || !metricId || status !== 'completed') {
      return;
    }

    const invalidationKeys = [['workstream', 'outcomes_validation', metricId]];
    console.log('Workstream outcomes validation event - invalidating keys:', invalidationKeys);
    invalidateKeys(invalidationKeys);
  },

  planning: (payload: Record<string, any>) => {
    const invalidationKeys = [];
    if (payload.id) {
      if (payload.query_key) {
        invalidationKeys.push(['planning', payload.query_key, payload.id]);
      } else {
        invalidationKeys.push(['planning', payload.id]);
      }
    } else {
      invalidationKeys.push(['planning', 'docs']);
    }
    console.log('Planning event - invalidating keys:', invalidationKeys);
    invalidateKeys(invalidationKeys);
  },

  scorecard: (payload: Record<string, any>) => {
    // A remote scorecard update can affect both the snapshot picker and any cached
    // snapshot's metric/status data. Refetch all of them instead of leaving an
    // inactive snapshot cache stale until the user navigates back to it.
    const invalidationKeys = [
      ['scorecards', 'snapshots'],
      ['scorecards', 'metrics'],
    ];

    if (!isProd) {
      console.log('Scorecard event - invalidating keys:', invalidationKeys, payload);
    }
    invalidationKeys.forEach((queryKey) => {
      void queryClient.invalidateQueries({queryKey, refetchType: 'all'});
    });
  },

  thread: (payload: Record<string, any>) => {
    const invalidationKeys = [];

    // If we have a specific collabId, invalidate threads for that document
    if (payload.collabId) {
      invalidationKeys.push(['document', 'threads', payload.collabId]);
    }

    // If we have a specific thread ID, invalidate that thread
    if (payload.threadId) {
      invalidationKeys.push(['document', 'thread', payload.threadId]);
    }

    // If no specific collabId is provided, invalidate all document threads
    if (invalidationKeys.length === 0) {
      invalidateKeys([['document', 'threads']]);
    } else {
      console.log('Thread event - invalidating keys:', invalidationKeys);
      invalidateKeys(invalidationKeys);
    }
  },
};

// Main handler for stateless messages from the Hocuspocus server
export const handleServerMessage = (event: {payload: string}) => {
  try {
    const message: BroadcastMessage = JSON.parse(event.payload);
    const handler = eventHandlers[message.type];

    if (!isProd) {
      console.log(`[WEBSOCKET EVENT]: ${message.type}`);
    }

    if (handler) {
      handler(message.payload);
    } else {
      console.warn(`[WEBSOCKET EVENT]: ${message.type} — No handler found`, message.payload);
    }
  } catch (parseError) {
    console.error(
      '[WEBSOCKET EVENT]: Error parsing stateless message:',
      parseError,
      'Raw payload:',
      event.payload
    );
  }
};
